import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/apiService';
import Footer from '../components/Footer';
import Header from '../components/Header';
import './MyOrdersPage.css';

const MyOrdersPage = () => {
  const { userId } = useAuth();
  const navigate = useNavigate();
  const { state } = useLocation();
  const [successMessage, setSuccessMessage] = useState(state?.orderBooked ? 'Your order has been successfully booked!' : '');
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [orderDetails, setOrderDetails] = useState({});
  const [loadingDetails, setLoadingDetails] = useState({});

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setIsLoading(true);
        const res = await api.get('/orders', { user_id: userId });
        const sorted = (res.data || []).sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );
        setOrders(sorted);
      } catch (err) {
        setError('Failed to load orders. Please try again.');
        console.error('Error fetching orders:', err);
      } finally {
        setIsLoading(false);
      }
    };
    if (userId) fetchOrders();
  }, [userId]);

  const handleToggleExpand = async (order) => {
    if (expandedOrderId === order.id) {
      setExpandedOrderId(null);
      return;
    }
    setExpandedOrderId(order.id);
    if (orderDetails[order.id]) return;

    setLoadingDetails(prev => ({ ...prev, [order.id]: true }));
    try {
      const [itemsRes, partnerRes] = await Promise.all([
        api.get('/order-items', { order_id: order.id }),
        order.partner_id ? api.get(`/service-partners/${order.partner_id}`) : Promise.resolve(null),
      ]);
      setOrderDetails(prev => ({
        ...prev,
        [order.id]: {
          items: itemsRes.data || [],
          partner: partnerRes ? partnerRes.data : null,
        },
      }));
    } catch (err) {
      console.error('Error fetching order details:', err);
    } finally {
      setLoadingDetails(prev => ({ ...prev, [order.id]: false }));
    }
  };

  const parseAddress = (address) => {
    if (!address) return null;
    if (typeof address === 'string') { try { return JSON.parse(address); } catch { return null; } }
    return address;
  };

  const maskPhone = (phone) => {
    if (!phone) return 'N/A';
    return phone.replace(/(\d{2})\d{6}(\d{2})/, '$1******$2');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    });
  };

  const getStatusBadge = (status, type) => {
    const orderBadges = {
      pending: { text: 'Pending', cls: 'badge-pending' },
      confirmed: { text: 'Confirmed', cls: 'badge-confirmed' },
      in_progress: { text: 'In Progress', cls: 'badge-progress' },
      completed: { text: 'Completed', cls: 'badge-completed' },
      cancelled: { text: 'Cancelled', cls: 'badge-cancelled' },
    };
    const workBadges = {
      not_started: { text: 'Not Started', cls: 'badge-ready' },
      started: { text: 'In Progress', cls: 'badge-progress' },
      completed: { text: 'Done', cls: 'badge-completed' },
    };
    const map = type === 'work' ? workBadges : orderBadges;
    const b = map[status] || { text: status, cls: 'badge-default' };
    return <span className={`status-badge ${b.cls}`}>{b.text}</span>;
  };

  return (
    <div className="my-orders-page">
      <div className="my-orders-container">
        <Header
          slogan="My Orders"
          rightContent={<button className="back-btn" onClick={() => navigate('/home')}>← Back</button>}
        />

        <main className="orders-content">
          {successMessage && (
            <div className="alert alert-success">
              ✅ {successMessage}
              <button className="alert-close" onClick={() => setSuccessMessage('')}>×</button>
            </div>
          )}
          {isLoading ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Loading your orders...</p>
            </div>
          ) : error ? (
            <div className="alert alert-error">{error}</div>
          ) : orders.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📋</div>
              <h3>No orders yet</h3>
              <p>Book a service to get started.</p>
              <button className="book-btn" onClick={() => navigate('/home')}>Browse Services</button>
            </div>
          ) : (
            <div className="orders-list">
              {orders.map((order) => {
                const isExpanded = expandedOrderId === order.id;
                const details = orderDetails[order.id];
                const isLoadingDetail = loadingDetails[order.id];
                const address = parseAddress(order.address);

                return (
                  <div key={order.id} className={`order-card ${isExpanded ? 'expanded' : ''}`}>
                    <div className="order-card-header" onClick={() => handleToggleExpand(order)}>
                      <div className="order-card-top">
                        <h3 className="order-id">Order # {order.id} </h3>
                        <div className="order-badges">
                          {getStatusBadge(order.status, 'order')}
                          {getStatusBadge(order.work_status, 'work')}
                        </div>
                      </div>
                      <div className="order-card-summary">
                        <span>📅 {formatDate(order.scheduled_date)}</span>
                        <span>🕐 {order.scheduled_time_label}</span>
                        <span className="order-amount">₹{order.total_amount}</span>
                      </div>
                      <span className="expand-icon">{isExpanded ? '▲' : '▼'}</span>
                    </div>

                    {isExpanded && (
                      <div className="order-detail">
                        {isLoadingDetail ? (
                          <div className="detail-loading"><div className="spinner small"></div> Loading details...</div>
                        ) : (
                          <>
                            <section className="detail-section">
                              <h4>📍 Service Address</h4>
                              {address ? (
                                <div className="address-display">
                                  {address.streetAddress && <div>{address.streetAddress}</div>}
                                  {(address.floor || address.flatNumber) && (
                                    <div>{address.floor && `${address.floor}, `}{address.flatNumber}</div>
                                  )}
                                  {address.apartment && <div>{address.apartment}</div>}
                                  {address.city && <div>{address.city}</div>}
                                  {address.pin && <div>PIN: {address.pin}</div>}
                                  {address.landmark && <div style={{ fontStyle: 'italic', color: '#666' }}>Landmark: {address.landmark}</div>}
                                </div>
                              ) : <p className="na-text">Address not available</p>}
                            </section>

                            <section className="detail-section">
                              <h4>💳 Payment</h4>
                              <div className="payment-row">
                                <span>Method: <strong>{order.payment_method}</strong></span>
                                <span>Status: {getStatusBadge(order.payment_status, 'order')}</span>
                              </div>
                              {order.final_amount && (
                                <div className="payment-row">
                                  <span>Initial: ₹{order.total_amount}</span>
                                  <span>Final: <strong>₹{order.final_amount}</strong></span>
                                </div>
                              )}
                            </section>

                            {details && details.items.length > 0 && (
                              <section className="detail-section">
                                <h4>🛠️ Services Booked</h4>
                                <table className="services-table">
                                  <thead>
                                    <tr>
                                      <th>Service</th>
                                      <th>Category</th>
                                      <th>Hrs</th>
                                      <th>₹/hr</th>
                                      <th>Total</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {details.items.map((item) => (
                                      <tr key={item.id}>
                                        <td>{item.service_name}</td>
                                        <td>{item.category?.replace('Services', '').replace(/([A-Z])/g, ' $1').trim()}</td>
                                        <td>{item.quantity}</td>
                                        <td>₹{item.price_per_hour}</td>
                                        <td>₹{item.total_price}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </section>
                            )}

                            <section className="detail-section">
                              <h4>👤 Service Provider</h4>
                              {!order.partner_id ? (
                                <p className="na-text">Not yet assigned</p>
                              ) : !details ? (
                                <p className="na-text">Loading...</p>
                              ) : details.partner ? (
                                <div className="partner-info">
                                  <div className="partner-name">{details.partner.name}</div>
                                  {details.partner.business_name && (
                                    <div className="partner-meta">{details.partner.business_name}</div>
                                  )}
                                  <div className="partner-meta">
                                    📞 {maskPhone(details.partner.phone_number)}
                                    {details.partner.average_rating && (
                                      <span className="partner-rating"> ★ {Number(details.partner.average_rating).toFixed(1)}</span>
                                    )}
                                  </div>
                                  {details.partner.total_jobs_completed > 0 && (
                                    <div className="partner-meta">{details.partner.total_jobs_completed} jobs completed</div>
                                  )}
                                </div>
                              ) : (
                                <p className="na-text">Provider details unavailable</p>
                              )}
                            </section>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </main>
      </div>
      <Footer />
    </div>
  );
};

export default MyOrdersPage;
